/**
 * API Service for Supabase Backend Communication
 * Handles all communication with the FastAPI backend
 */

import { API_BASE_URL } from '../config/apiConfig';
import { SessionManager } from '../redux/sessionManager';
import { store } from '../redux/store';
import { performGlobalLogout } from '../utils/logoutUtils';

// Hardcoded tenant ID from environment
const DEFAULT_TENANT_ID = process.env.REACT_APP_TENANT_ID || '10000000-1000-1000-1000-100000000001';

export interface Tenant {
  tenant_id: string;
  tenant_name: string;
  tenant_code: string;
  status: string;
  subscription_tier: string;
}

export interface Portfolio {
  portfolio_id: string;
  portfolio_name: string;
  portfolio_code: string;
  tenant_id: string;
  description: string;
  portfolio_type: string;
  status: string;
}

export interface Site {
  site_id: string;
  site_name: string;
  site_code: string;
  portfolio_id: string;
  tenant_id: string;
  location: string | {
    type: string;
    coordinates: [number, number];
  } | null; // Can be string "lat,lng" or GeoJSON object or null
  site_capacity_mw: number;
  commissioning_date: string | null;
  site_type: string;
  status: string;
  portfolio_name?: string;
  tenant_name?: string;
}

export interface EntityType {
  entity_type_id: string;
  entity_type_name: string;
  pvcollada_component_type: string;
  immutable_properties: Record<string, any>;
  mutable_schema: Record<string, any>;
}

export interface Entity {
  id: string; // Backend uses 'id' not 'entity_id'
  name: string; // Backend uses 'name' not 'entity_name'
  concept_id: string; // Backend uses 'concept_id' not 'entity_type_id'
  site_id: string;
  tenant_id: string;
  parent_entity_id: string | null;
  description: string | null;
  manufacturer: string | null;
  model: string | null;
  serial_number: string | null;
  installation_date: string | null;
  warranty_expiry: string | null;
  status: string;
  latitude: number | null;
  longitude: number | null;
  altitude: number | null;
  orientation: number | null;
  tilt: number | null;
  properties: Record<string, any>;
  version: number;
  created_at: string;
  updated_at: string;
  concept_name?: string;
  concept_category?: string;
  parent_entity_name?: string | null;
  child_count?: number;
  connection_count?: number;
  // Legacy fields for compatibility
  entity_id?: string;
  entity_name?: string;
  entity_type_id?: string;
  location?: any;
  asset_tag?: string | null;
  metadata?: Record<string, any>;
  entity_metadata?: Record<string, any>;  // Add entity_metadata field for ComponentSpecificationsTab
  entity_type_name?: string;
  pvcollada_component_type?: string;
  mutable_properties?: Record<string, any>;
  aliases?: string[];
  configuration?: Record<string, any>;
  component_order?: number | null;
  pref?: any;
}

export interface Concept {
  concept_id: string;
  concept_name: string;
  description: string;
  primary_category_id: string | null;
  usage_count: number;
  tenant_id: string;
  category_name?: string;
  category_path?: string[];
}

export interface EntityConnection {
  id: string;
  tenant_id: string;
  site_id: string;
  source_entity_id: string;
  target_entity_id: string;
  connection_type: string;
  properties?: Record<string, any>;
  configuration?: Record<string, any>;
  created_at: string;
  source_entity_name?: string;
  target_entity_name?: string;
}

export interface Event {
  event_id: string;
  tenant_id: string;
  site_id: string;
  title: string;
  summary: string | null;
  description: string | null;
  event_type: string;
  status: string;
  severity: string;
  event_date: string;
  resolved_at: string | null;
  resolution_summary: string | null;
  downtime_minutes: number | null;
  affected_entities: Array<{
    entity_name: string;
    entity_id: string;
    entity_type: string;
  }> | null;
  tags: Array<{
    tag_name: string;
    description: string;
  }> | null;
  created_at: string;
  updated_at: string;
  site_name?: string;
  duration_minutes?: number | null;
  is_overdue?: boolean;
  related_entity_count?: number;
  // Legacy fields for compatibility
  event_name?: string;
  entity_id?: string | null;
  event_timestamp?: string;
  metadata?: Record<string, any>;
  concept_id?: string;
  concept_name?: string;
  category_name?: string;
  entity_name?: string;
}

export interface DocumentType {
  document_type_id: string;
  type_name: string;
  description?: string;
  category: string;
}

export interface Document {
  document_id: string;
  document_type_id: string;
  title: string;
  description?: string;
  file_path: string;
  file_name: string;
  file_size_bytes: number;
  mime_type: string;
  site_id: string;
  tenant_id: string;
  portfolio_id?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  status: string;
  document_type_name?: string;
  tags?: Array<{ tag_category: string; tag_value: string }>;
}

class ApiService {
  private async fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    // Prepare headers with optional auth
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add any additional headers from options
    if (options?.headers) {
      if (options.headers instanceof Headers) {
        options.headers.forEach((value, key) => {
          headers[key] = value;
        });
      } else {
        Object.assign(headers, options.headers);
      }
    }

    // Add authorization header from SessionManager if available, otherwise use demo token
    const authHeaders = SessionManager.getAuthHeaders();
    if (authHeaders.Authorization) {
      headers['Authorization'] = authHeaders.Authorization;
    } else {
      headers['Authorization'] = 'Bearer demo-token';
    }

    try {
      const response = await fetch(url, {
        headers,
        ...options,
      });

      if (response.status === 401) {
        await performGlobalLogout(store.dispatch, {
          toastMessage: 'Session expired. Please sign in again.',
          toastType: 'error',
        });
        throw new Error('Unauthorized');
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (response.status === 204) {
        // No Content
        return undefined as T;
      }

      return (await response.json()) as T;
    } catch (error) {
      console.error(`API Error for ${endpoint}:`, error);
      throw error;
    }
  }

  // Tenant operations - Using hardcoded tenant
  async getTenants(): Promise<Tenant[]> {
    // Return hardcoded tenant since we're not using tenant selection
    return Promise.resolve([{
      tenant_id: DEFAULT_TENANT_ID,
      tenant_name: 'Default Solar Farm',
      tenant_code: 'DEFAULT',
      status: 'active',
      subscription_tier: 'standard'
    }]);
  }

  async getTenant(tenantId: string): Promise<Tenant> {
    // Return hardcoded tenant
    return Promise.resolve({
      tenant_id: tenantId || DEFAULT_TENANT_ID,
      tenant_name: 'Default Solar Farm',
      tenant_code: 'DEFAULT',
      status: 'active',
      subscription_tier: 'standard'
    });
  }

  // Portfolio operations
  async getPortfolios(tenantId?: string): Promise<Portfolio[]> {
    const endpoint = tenantId
      ? `/api/v1/portfolios/?tenant_id=${tenantId}`
      : '/api/v1/portfolios/';
    return this.fetchApi<Portfolio[]>(endpoint);
  }

  // Site operations
  // Microstation doesn't have a flat /sites endpoint - sites are nested under portfolios
  // So we need to: 1) get portfolios, 2) get sites for each portfolio
  async getSites(tenantId?: string, portfolioId?: string): Promise<Site[]> {
    try {
      // First, get all portfolios for the tenant
      const portfoliosEndpoint = tenantId
        ? `/api/v1/portfolios/?tenant_id=${tenantId}`
        : '/api/v1/portfolios/';

      const portfoliosResponse = await this.fetchApi<{ items: any[], total: number }>(portfoliosEndpoint);
      const portfolios = portfoliosResponse.items || [];

      if (portfolios.length === 0) {
        console.warn('No portfolios found');
        return [];
      }

      // Then, get sites for each portfolio
      const allSites: Site[] = [];
      for (const portfolio of portfolios) {
        try {
          const sitesEndpoint = `/api/v1/portfolios/${portfolio.id}/sites`;
          const sitesResponse = await this.fetchApi<{ items: any[], total: number }>(sitesEndpoint);

          // Map microstation response to frontend Site interface
          const mappedSites = (sitesResponse.items || []).map(site => ({
            site_id: site.id,
            site_name: site.name,
            site_code: site.code || '',
            portfolio_id: site.portfolio_id || portfolio.id,
            tenant_id: site.tenant_id || portfolio.tenant_id,
            location: site.address || null,
            site_capacity_mw: site.capacity_mw ? parseFloat(site.capacity_mw) : 0,
            commissioning_date: site.commissioning_date || null,
            site_type: site.site_type || 'solar',
            status: 'active',
            portfolio_name: portfolio.name,
            tenant_name: portfolio.tenant_name,
          }));

          allSites.push(...mappedSites);
        } catch (siteError) {
          console.warn(`Failed to fetch sites for portfolio ${portfolio.id}:`, siteError);
        }
      }

      return allSites;
    } catch (error) {
      console.warn('Failed to fetch sites via portfolios, returning empty array:', error);
      return [];
    }
  }

  async getSite(siteId: string): Promise<Site> {
    return this.fetchApi<Site>(`/api/v1/sites/${siteId}`);
  }

  async createSite(siteData: {
    site_name: string;
    site_code: string;
    portfolio_id: string;
    location?: string;
    site_capacity_mw?: number;
    commissioning_date?: string;
    site_type?: string;
    timezone?: string;
  }): Promise<Site> {
    return this.fetchApi<Site>('/api/v1/sites/', {
      method: 'POST',
      body: JSON.stringify(siteData),
    });
  }

  async updateSite(siteId: string, siteData: Partial<{
    site_name: string;
    site_code: string;
    location: string;
    site_capacity_mw: number;
    commissioning_date: string;
    site_type: string;
    status: string;
    timezone: string;
  }>): Promise<Site> {
    return this.fetchApi<Site>(`/api/v1/sites/${siteId}`, {
      method: 'PUT',
      body: JSON.stringify(siteData),
    });
  }

  // Entity Type operations
  async getEntityTypes(): Promise<EntityType[]> {
    return this.fetchApi<EntityType[]>('/api/v1/entity-types/');
  }

  // Entity operations
  // Uses microstation's portfolio-nested endpoint: /api/v1/portfolios/{portfolio_id}/sites/{site_id}/entities
  async getEntities(siteId?: string, tenantId?: string, portfolioId?: string): Promise<Entity[]> {
    try {
      if (!siteId || !portfolioId) {
        console.warn('getEntities requires both siteId and portfolioId for microstation API');
        return [];
      }

      const endpoint = `/api/v1/portfolios/${portfolioId}/sites/${siteId}/entities`;
      const limit = 1000; // Max allowed by backend
      let offset = 0;
      let allItems: any[] = [];
      let hasMore = true;

      while (hasMore) {
        const url = `${endpoint}?limit=${limit}&offset=${offset}`;
        const response = await this.fetchApi<{ items: any[], total: number, limit: number, offset: number }>(url);

        if (response.items && response.items.length > 0) {
          allItems = [...allItems, ...response.items];
          offset += response.items.length;

          // If we got fewer items than limit, we've reached the end
          if (response.items.length < limit) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }

      // Map microstation response fields to frontend Entity interface
      return allItems.map(entity => ({
        id: entity.uuid,
        name: entity.primary_id,
        concept_id: entity.component_type,
        site_id: entity.site_id,
        tenant_id: tenantId || '',
        parent_entity_id: null,
        description: null,
        manufacturer: null,
        model: null,
        serial_number: null,
        installation_date: null,
        warranty_expiry: null,
        status: 'active',
        latitude: null,
        longitude: null,
        altitude: null,
        orientation: null,
        tilt: null,
        properties: entity.properties || {},
        version: 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        // Legacy field mappings for compatibility
        entity_id: entity.uuid,
        entity_name: entity.primary_id,
        entity_type_id: entity.component_type,
        entity_type_name: entity.component_type,
        concept_name: entity.component_type,
        concept_category: entity.component_type,
        pvcollada_component_type: entity.component_type,
        metadata: entity.properties || {},
        entity_metadata: entity.properties || {},
        component_order: entity.component_order,
        aliases: entity.aliases || [],
        configuration: entity.configuration,
        pref: entity.pref,
      }));
    } catch (error) {
      console.warn('Entities endpoint not available, returning empty array:', error);
      return [];
    }
  }

  async getEntity(entityId: string): Promise<Entity> {
    const entity = await this.fetchApi<Entity>(`/api/v1/entities/${entityId}`);
    // Map backend fields to legacy fields
    return {
      ...entity,
      entity_id: entity.id,
      entity_name: entity.name,
      entity_type_id: entity.concept_id,
      entity_type_name: entity.concept_name,
      pvcollada_component_type: entity.concept_category,
      metadata: entity.properties || {},
      entity_metadata: entity.properties || {},  // Add entity_metadata mapping for ComponentSpecificationsTab
      asset_tag: entity.serial_number,
      location: entity.latitude && entity.longitude ? {
        type: 'Point',
        coordinates: [entity.longitude, entity.latitude]
      } : null
    };
  }

  // Concept operations
  async getConcepts(tenantId?: string): Promise<Concept[]> {
    try {
      // Fixed path - no double /concepts/
      return await this.fetchApi<Concept[]>('/api/v1/concepts/');
    } catch (error) {
      console.warn('Concepts endpoint not available, returning empty array');
      return [];
    }
  }

  // Connection operations
  // Uses microstation's portfolio-nested endpoint: /api/v1/portfolios/{portfolio_id}/sites/{site_id}/connections
  async getConnections(siteId?: string, portfolioId?: string): Promise<EntityConnection[]> {
    try {
      if (!siteId || !portfolioId) {
        console.warn('getConnections requires both siteId and portfolioId for microstation API');
        return [];
      }

      const endpoint = `/api/v1/portfolios/${portfolioId}/sites/${siteId}/connections`;
      const limit = 10000; // Max allowed by backend
      let offset = 0;
      let allItems: any[] = [];
      let hasMore = true;

      while (hasMore) {
        const url = `${endpoint}?limit=${limit}&offset=${offset}`;
        const response = await this.fetchApi<{ items: any[], total: number, limit: number, offset: number }>(url);

        if (response.items && response.items.length > 0) {
          allItems = [...allItems, ...response.items];
          offset += response.items.length;

          // If we got fewer items than limit, we've reached the end
          if (response.items.length < limit) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }

      // Map microstation response fields to frontend EntityConnection interface
      return allItems.map(conn => ({
        id: conn.id,
        tenant_id: '',
        site_id: conn.site_id,
        source_entity_id: conn.source_entity_id,
        target_entity_id: conn.target_entity_id,
        connection_type: conn.relation || 'connects',
        properties: {
          ...conn.properties,
          signal: conn.signal,
          pref: conn.pref,
        },
        configuration: conn.configuration, // Top-level for wire_class, Length, Voltage Drop
        created_at: new Date().toISOString(),
      }));
    } catch (error) {
      console.warn('Failed to fetch connections, returning empty array:', error);
      return [];
    }
  }

  // Event operations
  async getEvents(siteId?: string, tenantId?: string, conceptId?: string): Promise<Event[]> {
    try {
      const params = new URLSearchParams();
      if (siteId) params.append('site_id', siteId);
      if (conceptId) params.append('concept_id', conceptId);

      // Fixed path - no double /events/
      const endpoint = params.toString() ? `/api/v1/events/?${params.toString()}` : '/api/v1/events/';
      const events = await this.fetchApi<Event[]>(endpoint);

      // Map backend fields to legacy fields for compatibility
      return events.map(event => ({
        ...event,
        // Add legacy field mappings
        event_name: event.title,
        event_timestamp: event.event_date,
        metadata: event.tags || {},
      }));
    } catch (error) {
      console.warn('Events endpoint not available, returning empty array');
      return [];
    }
  }

  // Graph data operations (for network visualization)
  async getGraphData(siteId: string): Promise<{ nodes: any[], edges: any[] }> {
    return this.fetchApi<{ nodes: any[], edges: any[] }>(`/api/v1/graph/site/${siteId}`);
  }

  // Document operations
  async getDocuments(siteId?: string): Promise<Document[]> {
    try {
      const endpoint = siteId ? `/api/v1/documents/?site_id=${siteId}` : '/api/v1/documents/';
      return await this.fetchApi<Document[]>(endpoint);
    } catch (error) {
      console.warn('Documents endpoint not available, returning empty array');
      return [];
    }
  }

  async getDocumentTypes(): Promise<DocumentType[]> {
    try {
      return await this.fetchApi<DocumentType[]>('/api/v1/documents/types/');
    } catch (error) {
      console.warn('Document types endpoint not available, returning defaults');
      return [
        { document_type_id: '1', type_name: 'Site Communications', description: 'Communications, reports, and announcements', category: 'communications' },
        { document_type_id: '2', type_name: 'Manuals', description: 'Technical manuals and documentation', category: 'manuals' },
        { document_type_id: '3', type_name: 'As-Builts', description: 'As-built drawings and specifications', category: 'as-builts' }
      ];
    }
  }

  async uploadDocument(formData: FormData): Promise<any> {
    // Use FormData directly without JSON headers
    const headers: Record<string, string> = {};
    // Add authorization header from SessionManager if available, otherwise use demo token
    const authHeaders = SessionManager.getAuthHeaders();
    if (authHeaders.Authorization) {
      headers['Authorization'] = authHeaders.Authorization;
    } else {
      headers['Authorization'] = 'Bearer demo-token';
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/documents/upload`, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  }

  // Health check
  async healthCheck(): Promise<{ status: string; agent_ready: boolean }> {
    return this.fetchApi<{ status: string; agent_ready: boolean }>('/health');
  }
}

// Create singleton instance
export const apiService = new ApiService();

// Utility functions for data processing
export const processEntitiesForMap = (entities: Entity[]): any => {
  // Convert entities with location data to map features
  const features = entities
    .filter(entity => (entity.location || (entity.latitude && entity.longitude)))
    .map(entity => {
      // Use location if available, otherwise create from lat/lng
      const geometry = entity.location || {
        type: 'Point',
        coordinates: [entity.longitude!, entity.latitude!]
      };

      return {
        type: 'Feature',
        properties: {
          label: entity.entity_name || entity.name,
          type: entity.pvcollada_component_type || entity.entity_type_name || entity.concept_category,
          entity_id: entity.entity_id || entity.id,
          asset_tag: entity.asset_tag || entity.serial_number,
          installation_date: entity.installation_date,
          metadata: entity.metadata || entity.properties,
        },
        geometry
      };
    });

  return {
    type: 'FeatureCollection',
    features
  };
};

export const processEventsForTimeline = (events: Event[]): any[] => {
  // Group events by type and status for timeline display
  const groupedEvents: { [key: string]: Event[] } = {};

  events.forEach(event => {
    const key = event.event_type + ' - ' + event.status;

    if (!groupedEvents[key]) {
      groupedEvents[key] = [];
    }
    groupedEvents[key].push(event);
  });

  // Convert to timeline format
  return Object.entries(groupedEvents).map(([groupName, groupEvents]) => ({
    group: groupName,
    events: groupEvents.sort((a, b) =>
      new Date(b.event_date || b.event_timestamp || '').getTime() -
      new Date(a.event_date || a.event_timestamp || '').getTime()
    )
  }));
};

export const processEntitiesForNetwork = (entities: Entity[]): { nodes: any[], edges: any[] } => {
  // Convert entities to network nodes for visualization
  const nodes = entities.map(entity => ({
    data: {
      id: entity.entity_id || entity.id,
      label: entity.entity_name || entity.name,
      type: entity.pvcollada_component_type || entity.entity_type_name || entity.concept_category,
      entity_type_id: entity.entity_type_id || entity.concept_id,
      metadata: entity.metadata || entity.properties,
      parent_id: entity.parent_entity_id,
      status: entity.status,
    }
  }));

  // Create edges based on parent-child relationships
  const edges: any[] = [];
  entities.forEach(entity => {
    if (entity.parent_entity_id) {
      edges.push({
        data: {
          id: `${entity.parent_entity_id}-${entity.id}`,
          source: entity.parent_entity_id,
          target: entity.entity_id || entity.id,
          label: 'parent-child'
        }
      });
    }
  });

  return { nodes, edges };
};